import { http, apiCall } from './http'
import { v4 as uuid } from 'uuid'
import {
    CallbotActivation,
    CallbotConfiguration,
    CallbotMaterial,
    CallbotRecipientList,
    CallbotReportPeriodDays,
    CallbotReportSummary,
    CallbotRun,
    CallbotTask,
    CallbotTaskAction,
    CallbotTaskFilter,
    CallbotTaskGroup,
    CallbotTaskList,
    CallbotTelephonyEntry,
    CallbotTelephonyHistoryTab,
    CallbotTelephonyQuote,
    CallbotTelephonyTopup,
} from '@/types/callbot'

export const createCallbotCommandKey = () => uuid()
const idempotencyHeaders = (key: string) => ({ 'Idempotency-Key': key })

export function getCallbotActivations(projectId: string) {
    return apiCall<CallbotActivation[]>(() =>
        http.get('callbot/activations', { searchParams: { projectId } })
    )
}

export function getCallbotConfiguration(projectId: string) {
    return apiCall<CallbotConfiguration>(() =>
        http.get('callbot/configuration', { searchParams: { projectId } })
    )
}

export function getCallbotRuns(projectId: string, limit = 30) {
    return apiCall<{ items: CallbotRun[]; nextCursor: string | null }>(() =>
        http.get('callbot/runs', { searchParams: { projectId, limit } })
    )
}

export function getCallbotRun(projectId: string, runId: string) {
    return apiCall<CallbotRun>(() => http.get(`callbot/runs/${runId}`, { searchParams: { projectId } }))
}

export function getCallbotRunStatus(projectId: string, runId: string) {
    return apiCall<CallbotRun>(() =>
        http.get(`callbot/runs/${runId}/status`, { searchParams: { projectId } })
    )
}

export function getCallbotRunRecipients(projectId: string, runId: string, limit = 40, cursor?: string) {
    return apiCall<CallbotRecipientList>(() =>
        http.get(`callbot/runs/${runId}/recipients`, {
            searchParams: {
                projectId,
                limit,
                ...(cursor ? { cursor } : {}),
            },
        })
    )
}

export function getCallbotTasks(
    projectId: string,
    group: CallbotTaskGroup,
    filter: CallbotTaskFilter,
    limit = 40,
    cursor?: string
) {
    return apiCall<CallbotTaskList>(() =>
        http.get('callbot/tasks', {
            searchParams: {
                projectId,
                group,
                filter,
                limit,
                ...(cursor ? { cursor } : {}),
            },
        })
    )
}

export function getCallbotReportSummary(
    projectId: string,
    periodDays: CallbotReportPeriodDays,
    runId?: string
) {
    return apiCall<CallbotReportSummary>(() =>
        http.get('callbot/reports/summary', {
            searchParams: {
                projectId,
                periodDays,
                ...(runId ? { runId } : {}),
            },
        })
    )
}

export type CallbotTaskAttempt = {
    attemptNo: number
    state: string
    result: string | null
    ivrDigit: string | null
    startedAt: string | null
    answeredAt: string | null
    managerAnsweredAt: string | null
    finishedAt: string | null
    transferConnected: boolean | null
    transferDurationSeconds: number | null
}

export function getCallbotTask(projectId: string, taskId: string) {
    return apiCall<CallbotTask & { attempts: CallbotTaskAttempt[] }>(() =>
        http.get(`callbot/tasks/${taskId}`, {
            searchParams: { projectId },
        })
    )
}

export function updateCallbotTask(
    projectId: string,
    taskId: string,
    status: CallbotTaskAction,
    dueAt?: string,
    commandKey = createCallbotCommandKey()
) {
    return apiCall<{ taskId: string; group: CallbotTaskGroup | null; dueAt: string | null }>(() =>
        http.post(`callbot/tasks/${taskId}/status`, {
            headers: idempotencyHeaders(commandKey),
            json: {
                projectId,
                status,
                ...(dueAt ? { dueAt } : {}),
            },
        })
    )
}

export function prepareCallbotRun(
    input: {
        projectId: string
        name: string
        sourceActivationId: number
        audience?: {
            type: 'lost' | 'hot'
            lastVisitFrom?: number
            lastVisitTo?: number
        }
    },
    commandKey = createCallbotCommandKey()
) {
    return apiCall<CallbotRun>(() =>
        http.post('callbot/runs/drafts', {
            headers: idempotencyHeaders(commandKey),
            json: input,
        })
    )
}

export function recalculateCallbotRun(
    projectId: string,
    runId: string,
    commandKey = createCallbotCommandKey()
) {
    return apiCall<CallbotRun>(() =>
        http.post(`callbot/runs/${runId}/recalculate`, {
            headers: idempotencyHeaders(commandKey),
            json: { projectId },
        })
    )
}

export function startCallbotRun(
    input: {
        projectId: string
        runId: string
        timezone: string
        slots: Array<'morning' | 'day' | 'evening'>
        customWindow?: { from: string; to: string }
        startAt?: string
        maxAttempts: number
        retryIntervalMinutes: number
        transferPhone: string
        notification?: { connectorId: string; channelType: string }
        moduleA: { enabled: boolean; reminderMinutes: number }
    },
    commandKey = createCallbotCommandKey()
) {
    const { runId, ...body } = input
    return apiCall<CallbotRun>(() =>
        http.post(`callbot/runs/${runId}/start`, {
            headers: idempotencyHeaders(commandKey),
            json: body,
        })
    )
}

export function updateCallbotRunSettings(
    input: {
        projectId: string
        runId: string
        name?: string
        timezone?: string
        slots?: Array<'morning' | 'day' | 'evening'>
        customWindow?: { from: string; to: string }
        maxAttempts?: number
        retryIntervalMinutes?: number
        transferPhone?: string
        notification?: { connectorId: string; channelType: string } | null
        moduleA?: { enabled: boolean; reminderMinutes: number }
    },
    commandKey = createCallbotCommandKey()
) {
    const { runId, ...body } = input
    return apiCall<CallbotRun>(() =>
        http.post(`callbot/runs/${runId}/settings`, {
            headers: idempotencyHeaders(commandKey),
            json: body,
        })
    )
}

export function deleteCallbotRun(projectId: string, runId: string) {
    return apiCall<{ runId: string; deleted: boolean }>(() =>
        http.delete(`callbot/runs/${runId}`, {
            searchParams: { projectId },
        })
    )
}

export function saveCallbotDraft(
    input: {
        projectId: string
        runId: string
        name?: string
        draftState: Record<string, unknown>
    },
    commandKey = createCallbotCommandKey(),
    // keepalive — сохранение при закрытии вкладки: браузер доотправит запрос.
    options: { keepalive?: boolean } = {}
) {
    const { runId, ...body } = input
    return apiCall<CallbotRun>(() =>
        http.post(`callbot/runs/${runId}/draft`, {
            headers: idempotencyHeaders(commandKey),
            json: body,
            keepalive: options.keepalive,
        })
    )
}

export function prepareCallbotImportRun(
    input: { projectId: string; name: string; fileName: string },
    commandKey = createCallbotCommandKey()
) {
    return apiCall<CallbotRun>(() =>
        http.post('callbot/runs/drafts/import', {
            headers: idempotencyHeaders(commandKey),
            json: input,
        })
    )
}

export type CallbotAudienceImportResult = {
    run: CallbotRun
    fileName: string
    sourceRows: number
    accepted: number
    invalidPhones: number
    duplicatePhones: number
    samples: Array<{ rowNumber: number; value: string }>
}

export function uploadCallbotAudience(projectId: string, runId: string, file: File) {
    const body = new FormData()
    body.append('file', file)
    return apiCall<CallbotAudienceImportResult>(() =>
        http.post(`callbot/runs/${runId}/audience`, {
            // projectId обязан идти в query: роутерный гард читает его до
            // разбора multipart, когда req.body ещё пуст.
            searchParams: { projectId },
            body,
            timeout: false,
        })
    )
}

export function stopCallbotRun(projectId: string, runId: string, commandKey = createCallbotCommandKey()) {
    return apiCall<CallbotRun>(() =>
        http.post(`callbot/runs/${runId}/stop`, {
            headers: idempotencyHeaders(commandKey),
            json: { projectId },
        })
    )
}

export function resumeCallbotRun(projectId: string, runId: string, commandKey = createCallbotCommandKey()) {
    return apiCall<CallbotRun>(() =>
        http.post(`callbot/runs/${runId}/resume`, {
            headers: idempotencyHeaders(commandKey),
            json: { projectId },
        })
    )
}

export function getCallbotMaterials(projectId: string) {
    return apiCall<CallbotMaterial[]>(() => http.get('callbot/materials', { searchParams: { projectId } }))
}

function materialForm(name: string, file: File) {
    // Название — раньше файла: сервис читает поля multipart до файла.
    const body = new FormData()
    body.append('name', name)
    body.append('file', file)
    return body
}

export type CallbotMaterialCommandResult =
    | { data: CallbotMaterial; error: null; status: number }
    | { data: null; error: string; status: number }

// Статус нужен вызывающему коду: 409 означает разные вещи для загрузки
// и замены, а getError() у Nest отдаёт лишь «Conflict».
async function materialCommand(request: () => Promise<Response>): Promise<CallbotMaterialCommandResult> {
    try {
        const response = await request()
        const body = await response.json().catch(() => null)
        if (response.ok) return { data: body as CallbotMaterial, error: null, status: response.status }
        return {
            data: null,
            error: (body && (body.message || body.error)) || `${response.status}`,
            status: response.status,
        }
    } catch (error) {
        return { data: null, error: String(error), status: 0 }
    }
}

export function uploadCallbotMaterial(
    projectId: string,
    name: string,
    file: File,
    commandKey = createCallbotCommandKey()
) {
    return materialCommand(() =>
        http.post('callbot/materials', {
            searchParams: { projectId },
            headers: idempotencyHeaders(commandKey),
            body: materialForm(name, file),
        })
    )
}

export function replaceCallbotMaterial(
    projectId: string,
    materialId: string,
    name: string,
    file: File,
    commandKey = createCallbotCommandKey()
) {
    return materialCommand(() =>
        http.post(`callbot/materials/${materialId}/replace`, {
            searchParams: { projectId },
            headers: idempotencyHeaders(commandKey),
            body: materialForm(name, file),
        })
    )
}

// <audio src> не умеет слать Authorization, поэтому запись забираем
// запросом и отдаём плееру как object URL.
export async function getCallbotMaterialAudio(projectId: string, materialId: string): Promise<Blob | null> {
    const response = await http.get(`callbot/materials/${materialId}/audio`, {
        searchParams: { projectId },
    })
    return response.ok ? response.blob() : null
}

export function attachCallbotMaterial(
    projectId: string,
    runId: string,
    materialId: string,
    commandKey = createCallbotCommandKey()
) {
    return apiCall<{ sha256: string; durationSeconds: number; providerRecordId: string }>(() =>
        http.post(`callbot/runs/${runId}/material`, {
            headers: idempotencyHeaders(commandKey),
            json: { projectId, materialId },
        })
    )
}

export function getCallbotTelephonyBalance(projectId: string) {
    return apiCall<{ balanceRub: number }>(() =>
        http.get('callbot/telephony/balance', { searchParams: { projectId } })
    )
}

export function getCallbotTelephonyHistory(projectId: string, kind: CallbotTelephonyHistoryTab, since: string | null) {
    return apiCall<{ items: CallbotTelephonyEntry[] }>(() =>
        http.get('callbot/telephony/history', {
            searchParams: { projectId, kind, ...(since ? { since } : {}) },
        })
    )
}

export function getCallbotTelephonyQuote(projectId: string, recipients: number, timezone: string) {
    return apiCall<CallbotTelephonyQuote>(() =>
        http.get('callbot/telephony/quote', { searchParams: { projectId, recipients, timezone } })
    )
}

export function createCallbotTelephonyTopup(projectId: string, amountRub: number, key = createCallbotCommandKey()) {
    return apiCall<CallbotTelephonyTopup>(() =>
        http.post('callbot/telephony/topups', {
            headers: idempotencyHeaders(key),
            json: { projectId, amountRub },
        })
    )
}
